<template>
    <div id="dashboardSelection">
        <v-container>
            <div class="app-page-breadcrumbs">
                <AppBreadcrumbs label="Dashboards" :allow-all-sites="true" :site-id="breadcrumbSiteId" />
            </div>
            <v-row class="top-header">
                <v-col class="m12 l6 site-info">
                    <div class="site-image"
                        style="background: var(--card-primary-bg); display: flex; align-items: center; justify-content: center;">
                        <v-icon size="48" style="color: var(--app-primary-txt)">mdi-view-dashboard</v-icon>
                    </div>
                    <div class="site-title">
                        <h1 class="pageTitle">Dashboards</h1>
                        <p class="text--secondary">Create and manage custom monitoring dashboards</p>
                    </div>
                </v-col>
                <v-col class="m12 l6 d-flex justify-end align-center">
                    <v-btn class="haq-btn haq-btn-primary" @click="$emit('create-new')">
                        <v-icon class="mr-2">mdi-plus</v-icon><span>Create New Dashboard</span>
                    </v-btn>
                </v-col>
            </v-row>
            <!-- Search Section-->
            <v-row class="mb-4">
                <v-col cols="12" md="5">
                    <HaqTextField class="my-2" v-model="searchTerm" append-icon="mdi-magnify"
                        placeholder="Search Dashboards" hide-details single-line />
                </v-col>
            </v-row>
            <!-- Dashboard Grid-->
            <div class="db2-list" v-if="!dashboardLoading && filteredDashboards.length > 0">
                <div class="db2-list-grid">
                    <div
                        class="db2-list-card"
                        v-for="dashboard in filteredDashboards"
                        :key="dashboard.objectId"
                        @click="$emit('select-dashboard', dashboard.objectId)"
                    >
                        <div class="top">
                            <div class="icon"><v-icon>{{ dashboard.icon || 'mdi-view-dashboard' }}</v-icon></div>
                            <h3>{{ dashboard.name }}</h3>
                        </div>
                        <div class="desc">{{ dashboard.description || siteLabel(dashboard) }}</div>
                        <div class="foot" v-if="isAdmin">
                            <button type="button" class="db2-list-share" @click.stop="openShare(dashboard)">
                                <v-icon x-small>mdi-share-variant</v-icon>
                                Share
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="db2-list-skel" v-if="dashboardLoading">
                <div class="hc-skel" v-for="n in 6" :key="'skel-'+n">
                    <div class="hc-skel-line mid"></div>
                    <div class="hc-skel-line"></div>
                    <div class="hc-skel-line short"></div>
                </div>
            </div>
            <v-row v-if="shouldShowError">
                <v-col>
                    <div class="empty-card">
                        <div class="text-center">
                            <h4>Error loading dashboards</h4>
                            <p>{{ dashboardError }}</p>
                            <v-btn @click="loadDashboards" color="primary">Retry</v-btn>
                        </div>
                    </div>
                </v-col>
            </v-row>
            <v-row v-if="!dashboardLoading && !dashboardError && filteredDashboards.length === 0">
                <v-col>
                    <div class="empty-card">
                        <div class="text-center">
                            <h4>No dashboards found</h4>
                            <p v-if="searchTerm">Try adjusting your search terms or create a new dashboard</p>
                            <p v-else>Create your first dashboard to get started</p>
                        </div>
                    </div>
                </v-col>
            </v-row>
            <DashboardShareDialog
                v-model="shareOpen"
                :dashboard="shareDashboard"
                :project-id="projectId"
            />
        </v-container>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import HaqTextField from '@/components/form/HaqTextField.vue'
import { dashboardSiteId, isV2Dashboard } from './v2/dashboardModel'
import DashboardShareDialog from './v2/DashboardShareDialog.vue'
import './v2/dashboard-v2.sass'

export default {
    name: 'DashboardSelection',
    components: {
        AppBreadcrumbs,
        HaqTextField,
        DashboardShareDialog
    },
    data() {
        return {
            searchTerm: '',
            shareOpen: false,
            shareDashboard: null
        }
    },
    computed: {
        ...mapState({
            dashboards: state => state.dashboards,
            dashboardLoading: state => state.dashboardLoading,
            dashboardError: state => state.dashboardError,
            projects: state => state.projects,
            user: state => state.user
        }),
        currentProjectName() {
            // Get current project from store or route params
            const projectId = this.$route.params.projectId;
            if (projectId && this.projects.length > 0) {
                const currentProject = this.projects.find(p => p.objectId === projectId);
                return currentProject ? currentProject.name : 'Project';
            }
            // Fallback to first project or default
            return this.projects.length > 0 ? this.projects[0].name : 'Project';
        },
        /**
         * Site id for the All Sites breadcrumb (query only; default is All Sites).
         * @returns {string|null}
         */
        breadcrumbSiteId() {
            return this.$route.query.siteId || null;
        },
        /**
         * Route or first-project id for share catalogs.
         * @returns {string|null}
         */
        projectId() {
            return this.$route.params.projectId ||
                (this.projects.length > 0 ? this.projects[0].objectId : null);
        },
        /**
         * Admins may open the share dialog.
         * @returns {boolean}
         */
        isAdmin() {
            const role = this.user && this.user.role;
            return role === 'admin' || role === 'superAdmin';
        },
        /**
         * Dashboards matching search text and optional site filter.
         * @returns {Array}
         */
        filteredDashboards() {
            const siteId = this.breadcrumbSiteId;
            const needle = (this.searchTerm || '').toLowerCase();
            return this.dashboards.filter(dashboard => {
                const name = (dashboard.name || '').toLowerCase();
                const desc = (dashboard.description || '').toLowerCase();
                const matchesSearch = name.includes(needle) || desc.includes(needle);
                if (!matchesSearch) return false;
                if (!isV2Dashboard(dashboard)) return false;
                if (!siteId) return true;
                const sid = dashboardSiteId(dashboard);
                if (!sid) return true;
                return sid === siteId;
            })
        },
        // Clear error when we have dashboards or are loading
        shouldShowError() {
            return this.dashboardError && !this.dashboardLoading && this.filteredDashboards.length === 0;
        }
    },
    methods: {
        ...mapActions(['getDashboards']),
        async loadDashboards() {
            try {
                // Get projectId from route or store
                const projectId = this.$route.params.projectId ||
                    (this.projects.length > 0 ? this.projects[0].objectId : null);

                if (projectId) {
                    await this.getDashboards(projectId);
                    const role = this.user && this.user.role;
                    if (role === 'admin' || role === 'superAdmin') {
                        await this.$store.dispatch('purgeLegacyDashboards', { projectId });
                    }
                }
            } catch (error) {
                console.error('Failed to load dashboards:', error);
                window.M.toast({ html: 'Failed to load dashboards' });
            }
        },
        getLevelIcon(level) {
            switch (level) {
                case 'project': return 'mdi-layers'
                case 'site': return 'mdi-office-building'
                case 'zone': return 'mdi-map-marker'
                default: return 'mdi-layers'
            }
        },
        /**
         * Default site name, or a short fallback.
         * @param {object} dashboard
         * @returns {string}
         */
        siteLabel(dashboard) {
            const site = dashboard && dashboard.site;
            if (site && site.name) return site.name;
            const id = dashboardSiteId(dashboard);
            return id ? 'Default site set' : 'No default site';
        },
        /**
         * Open share for one list card.
         * @param {object} dashboard
         */
        openShare(dashboard) {
            this.shareDashboard = dashboard;
            this.shareOpen = true;
        },
        getLevelColor(level) {
            switch (level) {
                case 'project': return 'purple'
                case 'site': return 'primary'
                case 'zone': return 'green'
                default: return 'grey'
            }
        }
    },
    mounted() {
        this.loadDashboards();
    },
    watch: {
        // Clear error when dashboards are successfully loaded
        dashboards(newDashboards) {
            if (newDashboards && newDashboards.length > 0 && this.dashboardError) {
                this.$store.commit('setDashboardError', null);
            }
        },
        // Clear error when loading starts
        dashboardLoading(isLoading) {
            if (isLoading && this.dashboardError) {
                this.$store.commit('setDashboardError', null);
            }
        }
    }
}
</script>

<style lang="sass" scoped>
.no-flex
    display: block

.top-header
    margin-top: 16px
    margin-bottom: 0
    
    .site-info
        display: flex
        align-items: center
        gap: 16px
        margin-bottom: 20px
        
        .site-image
            width: 64px
            height: 64px
            border-radius: 12px
            display: flex
            align-items: center
            justify-content: center
            background: var(--card-primary-bg)
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
        
        .site-title
            h1.pageTitle
                color: var(--app-primary-txt)
                font-size: 28px
                font-weight: 400
                margin: 0
                line-height: 1.2
            
            p.text--secondary
                color: var(--app-tertiary-txt)
                font-size: 1rem
                margin: 0
                line-height: 1.4

// Search section styling
.input-field
    margin-bottom: 24px
    
    .haq-field
        width: 100%
        
        &::placeholder
            color: var(--app-tertiary-txt)
            opacity: 0.7

// Dashboard grid styling
.dashboardcard
    background: var(--card-primary-bg)
    border-radius: 12px
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1)
    transition: all 0.2s ease
    cursor: pointer
    height: 100%
    border: 1px solid var(--border)
    
    &:hover
        transform: translateY(-2px)
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15)
        border-color: var(--score-blue-color)
    
    .dashboardcard-header
        padding: 16px 16px 12px 16px
        border-bottom: 1px solid var(--border)
        display: flex
        justify-content: space-between
        align-items: flex-start
        
        .dashboardcard-title
            color: var(--app-primary-txt)
            font-size: 1rem
            font-weight: 600
            line-height: 1.2
            flex: 1
            margin-right: 8px
        
        .dashboardcard-icon
            color: var(--score-blue-color)
            flex-shrink: 0
    
    .dashboardcard-body
        padding: 12px 16px 16px 16px
        
        .dashboardcard-bodycontent
            .dashboardcard-measurement
                .dashboardcard-measurement-title-wrap
                    display: flex
                    justify-content: space-between
                    align-items: center
                    margin-bottom: 8px
                    
                    .dashboardcard-measurement-title
                        color: var(--app-tertiary-txt)
                        font-size: 0.85rem
                        font-weight: 500
                        flex: 1
                        margin-right: 6px
                    
                    .dashboardcard-measurement-icons
                        display: flex
                        align-items: center
                        gap: 3px
                        color: var(--app-tertiary-txt)
                        font-size: 0.75rem
                        
                        .v-icon
                            font-size: 14px
                            color: var(--score-blue-color)
                
                .dashboardcard-measurement-value
                    .dashboardcard-measurement-unit
                        color: var(--app-tertiary-txt)
                        font-size: 0.75rem
                        opacity: 0.8

// Empty states styling
.empty-card
    background: var(--card-primary-bg)
    border-radius: 12px
    padding: 60px 24px
    text-align: center
    border: 1px solid var(--border)
    
    .text-center
        h4
            color: var(--app-primary-txt)
            font-size: 1.5rem
            font-weight: 600
            margin: 0 0 16px 0
        
        p
            color: var(--app-tertiary-txt)
            font-size: 1rem
            line-height: 1.5
            margin: 0 0 24px 0
            max-width: 400px
            margin-left: auto
            margin-right: auto
        
        .v-progress-circular
            margin: 16px auto

// Button styling
.haq-btn
    background: var(--score-blue-color) !important
    color: white !important
    border-radius: 12px !important
    padding: 12px 24px !important
    font-weight: 500 !important
    text-transform: none !important
    box-shadow: 0 2px 8px rgba(78, 124, 255, 0.3) !important
    transition: all 0.2s ease !important
    
    &:hover
        background: var(--score-blue-color) !important
        opacity: 0.9 !important
        transform: translateY(-1px) !important
        box-shadow: 0 4px 12px rgba(78, 124, 255, 0.4) !important
    
    &:active
        transform: translateY(0) !important

// Responsive design
@media (max-width: 960px)
    .top-header
        .site-info
            flex-direction: column
            text-align: center
            gap: 12px

@media (max-width: 600px)
    .top-header
        .site-info
            .site-image
                width: 48px
                height: 48px
    
    .dashboardcard
        .dashboardcard-header
            padding: 16px 16px 12px 16px
            
            .dashboardcard-title
                font-size: 1rem
        
        .dashboardcard-body
            padding: 12px 16px 16px 16px
    
    .empty-card
        padding: 40px 16px
        
        .text-center
            h4
                font-size: 1.25rem
            
            p
                font-size: 0.9rem

// Animation for card loading
.dashboardcard
    animation: fadeInUp 0.3s ease-out

@keyframes fadeInUp
    from
        opacity: 0
        transform: translateY(20px)
    to
        opacity: 1
        transform: translateY(0)

// Grid layout improvements
.v-row
    margin: 0 -8px
    
    .v-col
        padding: 8px

// Search field focus improvements
.input-field
    position: relative
    
    &::after
        content: ''
        position: absolute
        bottom: 0
        left: 0
        right: 0
        height: 2px
        background: var(--score-blue-color)
        transform: scaleX(0)
        transition: transform 0.2s ease
    
    &:focus-within::after
        transform: scaleX(1)
</style>